import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import PauseIcon from 'icons/Pause.icon';
import useUndoActions from 'hooks/useUndoActions';
import UndoButton from 'components/UndoButton';
import { AvatarWithInitials } from 'components/AvatarWithInitials';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import useCallbackAsync from 'hooks/useCallbackAsync';
import useConfirmRevokeInOrder from 'router/subrouters/DataRoom/hooks/useConfirmRevokeInOrder';
import { getUserFullNameOrEmail } from 'utils';
import {
  getFormattedDate,
  DATE_FORMAT_DEFAULT,
  TIME_FORMAT_12H,
  isDateFromLastDays,
} from 'dateUtils';

import './UserWithAccess.styles.scss';

type UserWithAccessProps = {
  className?: string;
  nodeId: string;
  member: Partial<DataRoomMember>;
  onRevokeAccessSuccess?: () => void;
};

const UserWithAccess: React.FC<UserWithAccessProps> = (props) => {
  const { className, nodeId, member, onRevokeAccessSuccess } = props;

  const {
    _id: memberId,
    isLead,
    canRevoke,
    lastAccessNodeAt,
    hasAccessToOwnFiles,
    firstName,
    lastName,
    user,
  } = member || {};

  const [isRevokedAccess, setIsRevokedAccess] = useState(false);

  const classes = classNames('user-with-access', className);

  const canOnlyAccessOwnFiles = hasAccessToOwnFiles && isRevokedAccess;

  const { t } = useTranslation();

  const { isUndoAvailable, setUndoTimeout, clearUndoTimeout } =
    useUndoActions();

  const subtitle = useMemo(() => {
    if (canOnlyAccessOwnFiles) return t('revokedAccessForFilesNotUploadedUser');

    if (!lastAccessNodeAt) return t('notViewedYet');

    return t('viewedOn', {
      date: getFormattedDate(
        new Date(lastAccessNodeAt),
        isDateFromLastDays(new Date(lastAccessNodeAt), 1)
          ? TIME_FORMAT_12H
          : DATE_FORMAT_DEFAULT,
      ),
    });
  }, [canOnlyAccessOwnFiles, lastAccessNodeAt, t]);

  const { confirmRevoke } = useConfirmRevokeInOrder();

  const [onRevokeAccess, isRevokingAccess] = useCallbackAsync(async () => {
    if (!(await confirmRevoke([nodeId], isLead ? [memberId] : []))) return;

    await dataRoomApi.members.revokeAccessForNode(memberId, nodeId);

    setUndoTimeout(() => {
      onRevokeAccessSuccess();
      setIsRevokedAccess(true);
    });
  });

  const [onUndo, isUndoing] = useCallbackAsync(async () => {
    clearUndoTimeout();

    await dataRoomApi.members.undoRevokeAccessForNode(memberId, nodeId);
  });

  return (
    <div className={classes}>
      {/* Members who haven't set up an account have no user, but still have a name. */}
      <AvatarWithInitials user={user || { firstName, lastName }} />

      <div className="user-info">
        <p className="user-name">
          {getUserFullNameOrEmail(member) || t('unknownUser')}
        </p>

        <p
          className={classNames('subtitle', {
            'own-files-only': canOnlyAccessOwnFiles,
          })}
        >
          {subtitle}
        </p>
      </div>

      {canRevoke &&
        (isUndoAvailable ? (
          <UndoButton disabled={isUndoing} onUndo={onUndo} />
        ) : (
          <Button
            variant="link"
            icon={PauseIcon}
            disabled={isRevokingAccess}
            onClick={onRevokeAccess}
          >
            {t('revoke')}
          </Button>
        ))}
    </div>
  );
};

export default UserWithAccess;
